
import { Component, Prop, Ref, Watch } from 'vue-property-decorator';
import ListBase from '@/lib/listbase';
import SelectedDaily from './selected-daily/selected-daily.vue';
import DayRangePicker from '../components/day-range-picker.vue';
import { checkPermissions } from '../utils/check-permissions';
import ActionButtons from '../components/action-buttons.vue';
import ImportChanges from './selected-daily/import-changes.vue';
import SaveConfirmationDialog from '../components/save-confirmation-dialog.vue';

class PageFigureRequest {
    year: number = new Date().getFullYear();
    month: number = parseInt(new Date().toISOString().substr(5, 2));
    type: number | null = null;
}

interface DailyItem {
    id: number;
    name: string;
    days: { [key: number]: number };
    previousMonthTotal: number;
}

interface DealerData {
    id: number;
    name: string;
    models: string[];
    items: DailyItem[];
}

interface SelectedDailyRef extends Vue {
    showConfirmation: () => void;
    cancelImportedData: () => void;
    updateTableWithImportedData: (sheet: any) => void;
    exportData: () => void;
    showSummary: () => void;
    saveData: () => Promise<void>;
}

const VIEW_MAP = [
    { name: 'internal', label: 'Despachos AASA - Internos', index: 0 },
    { name: 'external', label: 'Despachos AASA - Externos', index: 1 },
    { name: 'total', label: 'Despachos AASA - Total', index: 2 },
];

@Component({
    components: {
        SelectedDaily,
        DayRangePicker,
        ActionButtons,
        ImportChanges,
        SaveConfirmationDialog,
    }
})
export default class Daily extends ListBase {
    @Prop({ type: String, default: '' }) readonly marcaSeleccionada!: string;

    tabModel: any = null;
    selectedOptions: string[] = [];
    filterOptions: string[] = [];
    allowMultipleSelection: boolean = false;

    dayRange: { start: number; end: number } = { start: 1, end: 1 };

    pagerequest: PageFigureRequest = new PageFigureRequest();
    dealers: DealerData[] = [];

    dataLoading: boolean = false;

    dailySalesData: any = null;
    salesLoaded: boolean = false;

    selectedDealers: number[] = [];
    dealerListFromBackend: Array<{ id: number; name: string }> = [];

    initialLoadDone: boolean = false;

    showImport: boolean = false;
    isEdited: boolean = false;
    confirmSave: boolean = false;
    @Ref('dailyInternal') readonly dailyInternal!: SelectedDailyRef;
    @Ref('dailyExternal') readonly dailyExternal!: SelectedDailyRef;
    @Ref('dailyTotal') readonly dailyTotal!: SelectedDailyRef;
    initialLoad = true;
    importType = 0;
    lastImportedType: 'internal' | 'external' | 'total' | null = null;
    importedSheets: Record<string, { headers: string[]; items: any[] } | null> = {
        internal: null, external: null, total: null
    };
    justImported: boolean = false;
    justEdited: boolean = false;

    @Watch('importType', { immediate: true })
    onImportTypeChanged(newVal: string | number, oldVal: string | number) {
        const entry = VIEW_MAP.find(tab => tab.index === newVal);
        this.lastImportedType = entry ? entry.name as 'internal' | 'external' | 'total' : null;
        this.tabModel = this.lastImportedType;
        console.log(`[Daily] 📋 importType cambió de`, oldVal, `a`, newVal);
    }

    get tabs() {
        return VIEW_MAP.filter(tab => (this.hasData as any)[tab.name]);
    }

    get brandId() {
        return this.marcaSeleccionada.split('|')[0];
    }

    get isDealer(): boolean {
        return false;
    }

    get dayRangeHeaders(): string[] {
        return Array
            .from({ length: this.dayRange.end - this.dayRange.start + 1 }, (_, i) =>
                (this.dayRange.start + i).toString()
            );
    }

    get activeDailyRef(): SelectedDailyRef | null {
        switch (this.tabModel) {
            case 'internal': return this.dailyInternal;
            case 'external': return this.dailyExternal;
            case 'total': return this.dailyTotal;
            default: return null;
        }
    }

    onChildEdit() {
        this.justEdited = true;
        this.justImported = false;
        this.isEdited = true;
        const entry = VIEW_MAP.find(tab => tab.index === this.importType);
        this.lastImportedType = entry
            ? (entry.name as 'internal' | 'external' | 'total')
            : null;
        this.tabModel = this.lastImportedType;
        console.log(`[Daily] ✏ onChildEdit → lastImportedType =`, this.lastImportedType);
    }

    onResumen() {
        this.activeDailyRef?.showSummary();
    }

    onSaveConfirm() {
        if (this.activeDailyRef) {
            this.activeDailyRef.showConfirmation();
        }
    }

    onCancelImport() {
        this.showImport = false;
        this.importedSheets = { internal: null, external: null, total: null };
        ['Internal', 'External', 'Total'].forEach(suffix => {
            const ref = (this.$refs as any)[`daily${suffix}`] as SelectedDailyRef | undefined;
            if (ref?.cancelImportedData) {
                ref.cancelImportedData();
            }
        });
        this.isEdited = false;
    }

    onUpdateDaily(sheet: { headers: string[]; items: any[] }) {
        const mapEntry = VIEW_MAP.find(tab => tab.index === this.importType)
        this.lastImportedType = mapEntry
            ? (mapEntry.name as 'internal' | 'external' | 'total')
            : null

        this.justImported = true;
        this.justEdited = false;
        this.importedSheets[this.importType] = sheet;

        ['Internal', 'External', 'Total'].forEach(suf => {
            const ref = (this.$refs as any)[`daily${suf}`] as SelectedDailyRef | undefined;
            if (ref?.updateTableWithImportedData) {
                ref.updateTableWithImportedData(sheet);
            }
        });

        this.isEdited = true;
    }

    onImportSuccess() {
        this.showImport = false;
        this.justImported = true;
        this.justEdited = false;
    }

    async onConfirmSave() {
        this.confirmSave = false;

        console.log(
            `[Daily] 💾 onConfirmSave — justImported:`, this.justImported,
            `justEdited:`, this.justEdited,
            `lastImportedType:`, this.lastImportedType,
            `tabModel:`, this.tabModel
        );


        let ref: SelectedDailyRef | null = null;

        if (this.justImported && this.lastImportedType) {
            ref = {
                internal: this.dailyInternal,
                external: this.dailyExternal,
                total: this.dailyTotal
            }[this.lastImportedType];
            this.justImported = false;
        } else if (this.justEdited) {
            ref = this.activeDailyRef;
            this.justEdited = false;
        } else {
            ref = this.activeDailyRef;
        }

        if (!ref || typeof ref.saveData !== 'function') {
            console.error('[Dispatches] ❌ No se encontró ref activa; justImported=', this.justImported);
            return;
        }

        console.log('👉 Voy a llamar a saveData() de:', ref);

        try {
            console.log('[Daily] ▶️ Llamando a ref.saveData() …');
            await ref.saveData();
            console.log('[Daily] ✅ saveData() completado');
            this.isEdited = false;
            this.justImported = false;
            this.justEdited = false;
            this.loadAllData();
        } catch (e) {
            console.error('[Daily] 💥 Error en saveData():', e);
        }
    }

    onUpdateObjectives({ type, sheet }: { type: 'internal' | 'external' | 'total'; sheet: { headers: string[]; items: any[] } }) {
        this.importedSheets[type] = sheet;
        const refMap = {
            internal: this.dailyInternal,
            external: this.dailyExternal,
            total: this.dailyTotal
        };
        refMap[type]?.updateTableWithImportedData(sheet);
        this.justImported = true;
        this.lastImportedType = type;
        this.isEdited = true;
    }

    handleDataUpdate(payload: { dealer: string; item: string; day: number; value: number }) {
        this.justEdited = true;
        this.justImported = false;
        this.$store.commit('updateDailyData', payload);
    }

    get listDealers() {
        return [{ id: 0, name: 'Todos' }, ...this.dealerListFromBackend];
    }

    get allDealersSelected() {
        const allDealerIds = this.dealerListFromBackend.map((d: any) => d.id);
        return this.selectedDealers.length === allDealerIds.length;
    }

    async changeDealer(ids: number[]) {
        const allDealerIds = this.listDealers
            .filter((d: any) => d.id !== 0)
            .map((d: any) => d.id);
        if (ids.includes(0) || ids.length === 0) {
            this.selectedDealers = allDealerIds;
        } else {
            this.selectedDealers = ids.filter((id: number) => id !== 0);
        }
        this.$store.commit('app/setDealer', this.selectedDealers);
    }

    removeDealer(dealer: any) {
        if (dealer.id === 0) {
            this.selectedDealers = [];
        } else {
            this.selectedDealers = this.selectedDealers.filter((id: number) => id !== dealer.id);
        }
        this.$store.commit('app/setDealer', this.selectedDealers);
    }

    get filteredDealers() {
        return this.dealers.filter(dealer =>
            this.selectedDealers.includes(dealer.id)
        );
    }

    get loading(): boolean {
        return this.dataLoading;
    }

    get currentMonthName(): string {
        const month = this.months.find(m => m.id === this.pagerequest.month);
        return month ? month.name : '';
    }

    get previousMonthName(): string {
        const prevMonthId = this.pagerequest.month === 1 ? 12 : this.pagerequest.month - 1;
        const month = this.months.find(m => m.id === prevMonthId);
        return month ? month.name : '';
    }

    get periods() {
        return this.gCommonList('years');
    }

    get months() {
        const list = this.gCommonList('months');
        list.forEach(m => {
            m['field'] = 'month_' + m.id;
        });
        return list;
    }

    get userRol() {
        return this.$store.state.session &&
            this.$store.state.session.user &&
            this.$store.state.session.user.roles
            ? this.$store.state.session.user.roles.map((role: any) => role.id)
            : [];
    }

    get userPermissions() {
        const roleIds = this.userRol;
        return checkPermissions(roleIds);
    }

    get hasView() {
        return this.userPermissions.view;
    }

    get hasAdmin() {
        return this.userPermissions.admin;
    }

    updateYear(newYear: number) {
        if (this.pagerequest.year !== newYear) {
            this.pagerequest.year = newYear;
            this.loadAllData();
        }
    }

    updateMonth(newMonth: number) {
        if (this.pagerequest.month !== newMonth) {
            this.pagerequest.month = newMonth;
            this.loadAllData();
        }
    }

    mounted() {
        if (this.marcaSeleccionada && !this.initialLoadDone) {
            const parts = this.marcaSeleccionada.split('|');
            if (parts[1]) {
                this.selectedOptions = [parts[1]];
                this.loadAllData();
                this.initialLoadDone = true;
            }
        }
    }

    get internalDealers(): DealerData[] {
        return this.filteredDealers.filter(dealer => {
            const nameUpper = dealer.name.toUpperCase();
            return nameUpper.includes('RM') || nameUpper.includes('REVO');
        });
    }

    get externalDealers(): DealerData[] {
        return this.filteredDealers.filter(dealer => {
            const nameUpper = dealer.name.toUpperCase();
            return !(nameUpper.includes('RM') || nameUpper.includes('REVO'));
        });
    }

    get hasData() {
        const hasInternal = this.internalDealers.length > 0;
        const hasExternal = this.externalDealers.length > 0;
        return {
            internal: hasInternal,
            external: hasExternal,
            total: hasInternal && hasExternal
        };
    }

    private resetTabModel() {
        if (this.hasData.internal) {
            this.tabModel = 'internal';
        } else if (this.hasData.external) {
            this.tabModel = 'external';
        } else {
            this.tabModel = '';
        }
    }

    handleDataSaved() {
        this.loadData();
    }

    async loadData() {
        if (!this.selectedOptions.length) {
            return;
        }

        this.dataLoading = true;

        const pad = (num: number) => (num < 10 ? '0' + num : num.toString());

        const mes_inicio = `${pad(this.dayRange.start)}/${pad(this.pagerequest.month)}/${this.pagerequest.year}`;
        const mes_fin = `${pad(this.dayRange.end)}/${pad(this.pagerequest.month)}/${this.pagerequest.year}`;

        const payload = {
            mes_inicio,
            mes_fin,
            marca: this.selectedOptions[0]
        };

        try {
            const response = await this.$store.dispatch('dailyModule/getDealerModels', payload);

            console.log('Respuesta de getDealerModels:', response);

            // 1. Mapear dealers desde el backend
            this.dealerListFromBackend = response.map((item: any, index: number) => ({
                id: index + 1,
                name: item.concesionario || 'Sin Nombre'
            }));

            // 2. Seleccionar todos los dealers por defecto
            const allDealerIds = this.dealerListFromBackend.map((d: any) => d.id);
            this.selectedDealers = allDealerIds;
            this.$store.commit('app/setDealer', this.selectedDealers);

            this.dealers = response.map((item: any, index: number) => ({
                id: index + 1,
                name: item.concesionario || 'Sin Nombre',
                models: item.modelos || [],
                items: []
            }));

            this.resetTabModel();
        } catch (error) {
            const err = error as any;
            console.error('Error en getDealerModels:', err?.message || err);
        }

        this.dataLoading = false;
    }

    private async loadAllData() {
        await this.loadData();
        const payload = {
            year: this.pagerequest.year,
            month: this.pagerequest.month,
            start_day: this.dayRange.start,
            end_day: this.dayRange.end,
            manufacter: this.selectedOptions[0]
        };
        this.dailySalesData = await this.$store.dispatch('dailyModule/getDailySales', payload);
        console.log(
            'Respuesta de getDailySales:',
            this.dailySalesData,
            ' con el anio', this.pagerequest.year,
            'y el mes', this.pagerequest.month
        );
    }

    public async updateDayRange(newRange: { start: number; end: number }): Promise<void> {
        if (this.dayRange.start !== newRange.start || this.dayRange.end !== newRange.end) {
            this.dayRange = newRange;
            this.salesLoaded = false;
            await this.loadAllData();
        }
    }

    public selectOption(option: string): void {
        if (!this.selectedOptions.includes(option)) {
            this.selectedOptions = [option];
            this.loadData();
        }
    }

    handleGoBack() {
        this.$emit('go-back');
    }
}
